import type { CustomFontSource } from '@stripe/stripe-js'

export const ADMIN_FONT_FAMILY = "'Poppins', ui-sans-serif, system-ui, sans-serif"

/** A bundled Poppins file, as Stripe's and our own `@font-face` rules read it. */
interface AdminFontSource extends CustomFontSource {
  display: string
  style: 'normal'
  weight: string
}

const adminFontAssets = [
  { url: '/fonts/poppins-latin-400-normal.woff2', weight: '400' },
  { url: '/fonts/poppins-latin-500-normal.woff2', weight: '500' },
  { url: '/fonts/poppins-latin-600-normal.woff2', weight: '600' },
  { url: '/fonts/poppins-latin-700-normal.woff2', weight: '700' },
]

function resolveAdminFontUrl(url: string) {
  if (typeof window === 'undefined') {
    return url
  }

  return new URL(url, window.location.origin).href
}

function isLoopbackAdminHost() {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname.endsWith('.localhost') ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1'
  )
}

export function getAdminFontSources(): CustomFontSource[] {
  // Public Stripe iframes cannot request fonts from the browser's loopback
  // address space. Keep local Stripe surfaces on their system fallback while
  // production uses the same bundled Poppins files as the rest of admin.
  if (isLoopbackAdminHost()) {
    return []
  }

  return getResolvedAdminFontSources()
}

function getResolvedAdminFontSources(): AdminFontSource[] {
  return adminFontAssets.map(({ url, weight }) => ({
    family: 'Poppins',
    src: `url(${resolveAdminFontUrl(url)})`,
    display: 'swap',
    style: 'normal',
    weight,
  }))
}

export function getAdminFontFaceCss(): string {
  return getResolvedAdminFontSources()
    .map(
      ({ family, src, display, style, weight }) =>
        `@font-face { font-family: '${family}'; src: ${src}; font-display: ${display}; font-style: ${style}; font-weight: ${weight}; }`
    )
    .join('\n')
}
